"use client"

import { useContext, useRef, useState } from "react"
import {
    Alert,
    AlertDescription,
    AlertTitle
} from "@papier/ui/components/ui/alert"
import { ShieldWarning as ShieldAlert } from "@papier/ui/components/icons"
import { AuthUIContext } from "../../../lib/auth-ui-provider"
import type { AuthLocalization } from "../../../localization/auth-localization"
import type { User } from "../../../types/auth-client"
import type { SettingsCardClassNames } from "../shared/settings-card"
import { SettingsCard } from "../shared/settings-card"
import { TwoFactorPasswordDialog } from "./two-factor-password-dialog"

export interface TwoFactorCardProps {
    className?: string
    classNames?: SettingsCardClassNames
    localization?: AuthLocalization
}

export function TwoFactorCard({
    className,
    classNames,
    localization
}: TwoFactorCardProps) {
    const {
        localization: contextLocalization,
        hooks: { useSession }
    } = useContext(AuthUIContext)

    const [showPasswordDialog, setShowPasswordDialog] = useState(false)
    const enableButton = useRef<HTMLButtonElement>(null)

    localization = { ...contextLocalization, ...localization }

    const { data: sessionData, isPending } = useSession()
    const isTwoFactorEnabled = (sessionData?.user as User)?.twoFactorEnabled
    const session = sessionData?.session as
        | { mfaRequired?: boolean | null }
        | undefined
    const isMfaEnrollmentRequired =
        session?.mfaRequired === true && !isTwoFactorEnabled

    return (
        <div className="grid gap-4">
            {isMfaEnrollmentRequired && (
                <Alert variant="warning">
                    <ShieldAlert />
                    <AlertTitle>Configurare obligatorie</AlertTitle>
                    <AlertDescription>
                        Autentificarea în doi pași este obligatorie pentru toți
                        utilizatorii. Vă rugăm să apăsați butonul „Activează
                        autentificarea în doi pași” și să urmați pașii de
                        configurare.
                    </AlertDescription>
                </Alert>
            )}

            <SettingsCard
                className={className}
                classNames={classNames}
                actionLabel={
                    isTwoFactorEnabled
                        ? undefined
                        : localization.ENABLE_TWO_FACTOR
                }
                description={localization.TWO_FACTOR_CARD_DESCRIPTION}
                instructions={
                    isTwoFactorEnabled
                        ? localization.TWO_FACTOR_ENABLED
                        : localization.TWO_FACTOR_ENABLE_INSTRUCTIONS
                }
                isPending={isPending}
                title={localization.TWO_FACTOR}
                actionButtonRef={enableButton}
                action={
                    isTwoFactorEnabled
                        ? undefined
                        : () => setShowPasswordDialog(true)
                }
            />

            <TwoFactorPasswordDialog
                classNames={classNames}
                open={showPasswordDialog}
                onOpenChange={setShowPasswordDialog}
                onCloseAutoFocus={(event) => {
                    event.preventDefault()
                    enableButton.current?.focus()
                }}
                isTwoFactorEnabled={!!isTwoFactorEnabled}
            />
        </div>
    )
}
